import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { REALMS, FERRIES, type Realm, type Skill, type SkillState } from '../data/realms';
import IslandCanvas, { type NavTarget } from '../components/IslandCanvas';
import RealmModal from '../components/RealmModal';
import SkillModal from '../components/SkillModal';
import { getSolvedSet } from '../lib/progress';
import { deriveRealmSkills, realmProgress, realmProblemProgress } from '../lib/derivation';
import { useFitScale } from '../lib/useFitScale';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { useNavigate } from 'react-router-dom';
import { useIsAdmin } from '../auth/AuthContext';
import ProblemEditorDrawer from '../CodingInterviewPage/ProblemEditorDrawer';
import type { Problem } from '../data/problems';

// Logical canvas dimensions. Every realm's x/y/w/h in `data/realms.ts` lives
// inside this box. The visible map is drawn at this size and then uniformly
// scaled down to whatever container width the viewport provides, so all seven
// islands stay fully on-screen across phones, laptops, and large displays.
const CANVAS_W = 1180;
const CANVAS_H = 620;

// Below this width, the spatial map becomes too cramped even when scaled, so
// we fall back to a vertical realm-card list — same data, same chip palette.
const MOBILE_BREAKPOINT = 760;

const REALM_TINT: Record<string, string> = {
  amber: 'var(--w-amber)',
  peach: 'var(--w-peach)',
  coral: 'var(--w-coral)',
  tan:   'var(--w-tan)',
  cream: 'var(--w-cream)',
};

const CHIP_FILL: Record<SkillState, string> = {
  done:      'var(--w-amber)',
  current:   'var(--w-coral)',
  attempted: 'var(--w-cream)',
  todo:      'var(--paper)',
  locked:    'transparent',
};

const CHIP_GLYPH: Record<SkillState, string> = {
  done:      '✓ ',
  current:   '▶ ',
  attempted: '◐ ',
  todo:      '',
  locked:    '🔒 ',
};

function realmCenter(r: Realm) {
  return { x: r.x + r.w / 2, y: r.y + r.h / 2 };
}

// Stable per-id silhouette in the 28–43% range — keeps the inscribed
// rectangle big enough that titles + chips never sit on the curve.
function islandRadius(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  const pick = (offset: number) => 28 + (Math.abs((h >> offset) & 0xf));
  return `${pick(0)}% ${pick(4)}% ${pick(8)}% ${pick(12)}% / ${pick(2)}% ${pick(6)}% ${pick(10)}% ${pick(14)}%`;
}

const SkillChip: React.FC<{ skill: Skill; onClick: () => void }> = ({ skill, onClick }) => {
  const isLocked = skill.state === 'locked';
  const isCurrent = skill.state === 'current';
  return (
    <span
      onClick={e => { e.stopPropagation(); if (!isLocked) onClick(); }}
      title={skill.pun}
      style={{
        display: 'inline-block',
        padding: '2px 7px',
        border: '1.2px solid var(--ink)',
        borderRadius: 18,
        fontSize: 9.5,
        fontWeight: 500,
        background: CHIP_FILL[skill.state],
        backgroundImage: isLocked
          ? 'repeating-linear-gradient(45deg, transparent 0 3px, rgba(0,0,0,0.12) 3px 4px)'
          : 'none',
        color: isCurrent ? '#fff' : 'var(--ink)',
        opacity: isLocked ? 0.55 : 1,
        cursor: isLocked ? 'not-allowed' : 'pointer',
        whiteSpace: 'nowrap',
        lineHeight: 1.4,
      }}
    >
      {CHIP_GLYPH[skill.state]}{skill.name.split(' ')[0]}
    </span>
  );
};

interface IslandCardProps {
  realm: Realm;
  skills: Skill[];
  problemsDone: number;
  problemsTotal: number;
  onClick: () => void;
  onSkillClick: (skillId: string) => void;
}

const IslandCard: React.FC<IslandCardProps> = ({ realm, skills, problemsDone, problemsTotal, onClick, onSkillClick }) => {
  const tint = REALM_TINT[realm.color] ?? 'var(--w-cream)';
  const body  = `color-mix(in srgb, ${tint} 55%, var(--paper))`;
  const beach = `color-mix(in srgb, ${tint} 35%, var(--paper))`;
  return (
    <div
      onClick={onClick}
      style={{
        position: 'absolute',
        left: realm.x,
        top: realm.y,
        width: realm.w,
        height: realm.h,
        borderRadius: islandRadius(realm.id),
        border: '1.6px solid var(--ink)',
        boxShadow: `inset 0 0 0 7px ${beach}, 3px 4px 0 var(--ink)`,
        background: body,
        padding: '24px 22px 18px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'transform 0.15s, box-shadow 0.15s',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLDivElement).style.transform = 'translate(-1px, -2px)';
        (e.currentTarget as HTMLDivElement).style.boxShadow = `inset 0 0 0 7px ${beach}, 4px 6px 0 var(--ink)`;
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLDivElement).style.transform = '';
        (e.currentTarget as HTMLDivElement).style.boxShadow = `inset 0 0 0 7px ${beach}, 3px 4px 0 var(--ink)`;
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <span style={{ fontSize: 20 }}>{realm.emoji}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 14, letterSpacing: '-0.02em', color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {realm.name}
          </div>
          <div style={{ fontSize: 9, fontStyle: 'italic', opacity: 0.8, color: 'var(--ink-soft)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            "{realm.pun}"
          </div>
        </div>
        <span
          style={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 9,
            padding: '1px 6px',
            border: '1px solid var(--ink)',
            borderRadius: 10,
            background: 'var(--paper)',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {problemsDone}/{problemsTotal}
        </span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignContent: 'flex-start', flex: 1, minHeight: 0 }}>
        {skills.map(s => (
          <SkillChip key={s.id} skill={s} onClick={() => onSkillClick(s.id)} />
        ))}
      </div>
    </div>
  );
};

const MobileRealmList: React.FC<{
  realms: Realm[];
  enriched: Map<string, { skills: Skill[]; done: number; total: number; problemsDone: number; problemsTotal: number }>;
  onRealmClick: (id: string) => void;
  onSkillClick: (skillId: string) => void;
}> = ({ realms, enriched, onRealmClick, onSkillClick }) => (
  <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
    {realms.map(realm => {
      const data = enriched.get(realm.id)!;
      const tint = REALM_TINT[realm.color] ?? 'var(--w-cream)';
      return (
        <div
          key={realm.id}
          className="sk-box shadow"
          onClick={() => onRealmClick(realm.id)}
          style={{
            padding: 14,
            cursor: 'pointer',
            background: `color-mix(in srgb, ${tint} 35%, var(--paper))`,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 22 }}>{realm.emoji}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 15, letterSpacing: '-0.02em' }}>{realm.name}</div>
              <div style={{ fontSize: 11, fontStyle: 'italic', color: 'var(--ink-soft)' }}>"{realm.pun}"</div>
            </div>
            <span className={`sk-pill ${realm.color}`}>{data.problemsDone} / {data.problemsTotal}</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {data.skills.map(s => (
              <SkillChip key={s.id} skill={s} onClick={() => onSkillClick(s.id)} />
            ))}
          </div>
        </div>
      );
    })}
  </div>
);

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isMobile;
}

const IslandMap: React.FC = () => {
  useDocumentTitle('island map');
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const [newOpen, setNewOpen] = useState(false);
  const fitRef = useRef<HTMLDivElement>(null);
  const scale = useFitScale(fitRef, CANVAS_W);

  // Modal stack — only two levels. The map itself is the base; openRealmId
  // layers Step 2 (the realm modal); openSkillId layers Step 3 on top of
  // that. Esc pops one level at a time.
  const [openRealmId, setOpenRealmId] = useState<string | null>(null);
  const [openSkillId, setOpenSkillId] = useState<string | null>(null);

  const popLevel = useCallback(() => {
    setOpenSkillId(prev => {
      if (prev !== null) return null;
      setOpenRealmId(null);
      return prev;
    });
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (openRealmId === null && openSkillId === null) return;
      popLevel();
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openRealmId, openSkillId, popLevel]);

  // `enriched` is computed once per mount. IslandMap unmounts when the user
  // navigates to the /problem/:id IDE and re-mounts on return, so solved
  // markers get picked up naturally without watching modal open/close state.
  const enriched = useMemo(() => {
    const solved = getSolvedSet();
    const m = new Map<string, { skills: Skill[]; done: number; total: number; problemsDone: number; problemsTotal: number }>();
    for (const realm of REALMS) {
      const skills = deriveRealmSkills(realm, solved);
      const { done, total } = realmProgress(realm, solved);
      const { done: problemsDone, total: problemsTotal } = realmProblemProgress(realm, solved);
      m.set(realm.id, { skills, done, total, problemsDone, problemsTotal });
    }
    return m;
  }, []);

  const navTargets = useMemo<NavTarget[]>(
    () => REALMS.map(r => ({ id: r.id, label: r.name, x: r.x, y: r.y, w: r.w, h: r.h, route: r.id })),
    [],
  );

  const openRealm = useCallback((id: string) => setOpenRealmId(id), []);
  const openSkill = useCallback((id: string) => setOpenSkillId(id), []);

  const activeRealm = openRealmId ? REALMS.find(r => r.id === openRealmId) ?? null : null;
  const activeRealmData = activeRealm ? enriched.get(activeRealm.id) ?? null : null;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div
        style={{
          padding: '10px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          borderBottom: '1.4px dashed var(--ink)',
          background: 'var(--paper)',
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em' }}>island world map</h2>
        <span style={{ flex: 1 }} />
        {isAdmin && (
          <button className="sk-pill" style={{ cursor: 'pointer', border: '1.4px solid var(--ink)' }} onClick={() => setNewOpen(true)}>
            ＋ new problem
          </button>
        )}
        <span className="sk-pill amber">⚡ daily challenges · 2 / 5 done</span>
        <span className="sk-pill coral">🏆 daily leaderboard</span>
      </div>
      {newOpen && (
        <ProblemEditorDrawer
          initial={null}
          onClose={() => setNewOpen(false)}
          onSaved={(saved: Problem) => { setNewOpen(false); navigate(`/problem/${saved.id}`); }}
        />
      )}

      {isMobile ? (
        <div className="scroll-y" style={{ flex: 1, background: 'var(--paper-2)' }}>
          <MobileRealmList
            realms={REALMS}
            enriched={enriched}
            onRealmClick={openRealm}
            onSkillClick={openSkill}
          />
        </div>
      ) : (
        <div
          ref={fitRef}
          style={{
            flex: 1,
            position: 'relative',
            overflow: 'auto',
            background: 'linear-gradient(180deg, var(--paper) 0%, var(--paper-2) 100%)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
          }}
        >
          <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, opacity: 0.25, pointerEvents: 'none' }}>
            <defs>
              <pattern id="waves" x="0" y="0" width="40" height="14" patternUnits="userSpaceOnUse">
                <path d="M 0 7 Q 10 3, 20 7 T 40 7" stroke="var(--ink)" strokeWidth="0.6" fill="none" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#waves)" />
          </svg>

          {/* The outer box reserves the *scaled* footprint so the document
              actually shrinks when scale < 1 (CSS transform alone doesn't
              affect layout). The inner box stays at the logical 1180×620
              size and is then scaled in place. */}
          <div
            style={{
              position: 'relative',
              width: CANVAS_W * scale,
              height: CANVAS_H * scale,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: CANVAS_W,
                height: CANVAS_H,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }}
            >
              <svg
                viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
                width={CANVAS_W}
                height={CANVAS_H}
                style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
              >
                {FERRIES.map((f, i) => {
                  const A = REALMS.find(r => r.id === f.from);
                  const B = REALMS.find(r => r.id === f.to);
                  if (!A || !B) return null;
                  const { x: ax, y: ay } = realmCenter(A);
                  const { x: bx, y: by } = realmCenter(B);
                  const mx = (ax + bx) / 2;
                  const my = (ay + by) / 2 - 20;
                  return (
                    <path
                      key={i}
                      d={`M ${ax} ${ay} Q ${mx} ${my}, ${bx} ${by}`}
                      stroke="var(--w-coral)"
                      strokeWidth="1.8"
                      strokeDasharray="6 4"
                      fill="none"
                      opacity="0.7"
                    />
                  );
                })}
              </svg>

              {REALMS.map(realm => {
                const data = enriched.get(realm.id)!;
                return (
                  <IslandCard
                    key={realm.id}
                    realm={realm}
                    skills={data.skills}
                    problemsDone={data.problemsDone}
                    problemsTotal={data.problemsTotal}
                    onClick={() => openRealm(realm.id)}
                    onSkillClick={openSkill}
                  />
                );
              })}

              <IslandCanvas
                width={CANVAS_W}
                height={CANVAS_H}
                targets={navTargets}
                onEnter={t => openRealm(t.route)}
                enabled={openRealmId === null}
              />

              <div
                style={{
                  position: 'absolute', right: 20, bottom: 20,
                  width: 56, height: 56,
                  border: '1.6px solid var(--ink)',
                  borderRadius: '50%',
                  background: 'var(--paper)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 16,
                  boxShadow: '2px 3px 0 var(--ink)',
                  flexDirection: 'column',
                }}
              >
                <span style={{ fontSize: 9, marginBottom: -2 }}>↑</span>
                N
              </div>

              <div className="sk-box" style={{ position: 'absolute', left: 16, bottom: 16, padding: 10, fontSize: 11, background: 'var(--paper)' }}>
                <div className="sk-anno" style={{ marginBottom: 6 }}>legend</div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 2 }}>
                  <span style={{ width: 10, height: 10, background: 'var(--w-amber)', border: '1px solid var(--ink)', display: 'inline-block' }} />
                  done
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
                  <span style={{ width: 10, height: 10, background: 'var(--w-coral)', border: '1px solid var(--ink)', display: 'inline-block' }} />
                  in progress
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
                  <span style={{ width: 10, height: 10, background: 'var(--paper)', border: '1px solid var(--ink)', display: 'inline-block' }} />
                  open
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal stack — rendered last so they overlay the map regardless of
          layout (mobile list or desktop spatial map). Esc handling lives in
          the parent useEffect so a single source of truth pops one level
          per keypress. */}
      {activeRealm && activeRealmData && (
        <RealmModal
          realm={activeRealm}
          skills={activeRealmData.skills}
          done={activeRealmData.done}
          total={activeRealmData.total}
          onClose={() => setOpenRealmId(null)}
          onOpenSkill={openSkill}
          paused={openSkillId !== null}
        />
      )}
      {openSkillId && (
        <SkillModal
          skillId={openSkillId}
          onClose={() => setOpenSkillId(null)}
        />
      )}
    </div>
  );
};

export default IslandMap;
